import React, { useState } from 'react'
import './style.scss'
import Button from '../../../../../Shared/Form/Button'
import Checkbox from '../../../../../Shared/Form/Checkbox'
import GenericModal from '../../../../../Shared/GenericModal'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { ReactComponent as AlertIcon } from '../../../../../../assets/img/icons/exclamation-alert.svg'
import feedbackIcon from '../../../../../../assets/img/icons/checklist.svg'
import thanksIcon from '../../../../../../assets/img/icons/appreciation.svg'
import questionIcon from '../../../../../../assets/img/icons/question.svg'

const T_BASE = 'openContentLink.descriptionForReview.feedbackForm'

const CHECKBOX_OPTIONS = [
  'incorrectInformation',
  'missingImportantInformation',
  'doesNotReflectHotelWell',
]

const DescriptionForReviewFeedbackForm = ({ existingFeedback, onSubmitFeedback, onCheckFactsheet }) => {
  const { t } = useTranslation()
  const [submitted, setSubmitted] = useState(!!existingFeedback?.feedbackData)
  const [showDeterrentModal, setShowDeterrentModal] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [showThanksModal, setShowThanksModal] = useState(false)
  const [feedbackData, setFeedbackData] = useState(() => ({
    incorrectInformation: existingFeedback?.feedbackData?.incorrectInformation ?? false,
    missingImportantInformation: existingFeedback?.feedbackData?.missingImportantInformation ?? false,
    doesNotReflectHotelWell: existingFeedback?.feedbackData?.doesNotReflectHotelWell ?? false,
    otherComment: existingFeedback?.feedbackData?.otherComment ?? '',
  }))

  const toggleOption = (option) => {
    setFeedbackData(prevFeedbackData => ({
      ...prevFeedbackData,
      [option]: !prevFeedbackData[option],
    }))
  }
  const setOtherComment = (comment) => {
    setFeedbackData(prevFeedbackData => ({ ...prevFeedbackData, otherComment: comment }))
  }
  const canSubmit = feedbackData.otherComment.trim() !== ''
  const handleSubmit = () => {
    if (!canSubmit) return
    onSubmitFeedback(feedbackData)
    setSubmitted(true)
    setShowModal(false)
    setShowThanksModal(true)
  }
  const proceedToReport = () => {
    setShowDeterrentModal(false)
    setShowModal(true)
  }
  const handleCheckFactsheet = () => {
    setShowDeterrentModal(false)
    onCheckFactsheet?.()
  }

  return (
    <div className="description-for-review-feedback">
      {
        !submitted && (
          <div className="description-for-review-feedback__banner">
            <Button
              variant="primary"
              minWidth={'200px'}
              classNames="description-for-review-feedback__report-btn"
              onClick={() => setShowDeterrentModal(true)}
            >
              <AlertIcon className="description-for-review-feedback__report-icon"/>
              {t(`${T_BASE}.banner.reportIssue`)}
            </Button>
          </div>
        )
      }

      <GenericModal
        id="description-for-review-deterrent-modal"
        className="description-feedback-modal description-deterrent-modal"
        show={showDeterrentModal}
        onClose={() => setShowDeterrentModal(false)}
        showCloseButton={true}
        closeButtonLabel={t(`${T_BASE}.buttons.close`)}
        icon={<img src={questionIcon} alt="" width="56" height="56"/>}
        iconPlacement="top"
        background={null}
        borderRadius="16px"
        maxWidth="400px"
        title={t(`${T_BASE}.deterrent.title`)}
        body={
          <div className="deterrent-modal__text">
            <p className="deterrent-modal__lead">{t(`${T_BASE}.deterrent.body`)}</p>
            <p>{t(`${T_BASE}.deterrent.question`)}</p>
          </div>
        }
        buttonsCentered={false}
        actions={[
          {
            label: `${T_BASE}.deterrent.reportIssue`,
            onClick: proceedToReport,
            variant: 'neutral',
          },
          {
            label: `${T_BASE}.deterrent.checkFactsheet`,
            onClick: handleCheckFactsheet,
            variant: 'primary',
          },
        ]}
      />

      <GenericModal
        id="description-for-review-feedback-modal"
        className="description-feedback-modal"
        show={showModal}
        onClose={() => setShowModal(false)}
        showCloseButton={true}
        closeButtonLabel={t(`${T_BASE}.buttons.close`)}
        icon={<img src={feedbackIcon} alt="" width="56" height="56"/>}
        iconPlacement="top"
        background={null}
        borderRadius="16px"
        maxWidth="400px"
        title={t(`${T_BASE}.title`)}
        subtitle={t(`${T_BASE}.subtitle`)}
        body={
          <div className="feedback-modal__fields">
            <div className="feedback-modal__options">
              {CHECKBOX_OPTIONS.map(option => (
                <Checkbox
                  key={option}
                  size={16}
                  onChange={() => toggleOption(option)}
                  value={feedbackData[option]}
                  label={t(`${T_BASE}.options.${option}`)}
                />
              ))}
            </div>
            <div className="feedback-modal__comment-field">
              <textarea
                className="feedback-modal__comment"
                value={feedbackData.otherComment}
                onChange={(e) => setOtherComment(e.target.value)}
                placeholder={t(`${T_BASE}.otherCommentPlaceholder`)}
                rows="3"
                required
                aria-describedby="feedback-modal-comment-hint"
              />
              {
                !canSubmit && <p
                  id="feedback-modal-comment-hint"
                  className="feedback-modal__comment-hint"
                >
                  {t(`${T_BASE}.otherCommentRequired`)}
                </p>
              }
            </div>
          </div>
        }
        actions={[
          {
            label: `${T_BASE}.buttons.submit`,
            onClick: handleSubmit,
            variant: 'primary',
            minWidth: '100%',
            disabled: !canSubmit,
          },
        ]}
      />

      <GenericModal
        id="description-for-review-thanks-modal"
        show={showThanksModal}
        onClose={() => setShowThanksModal(false)}
        showCloseButton={true}
        closeButtonLabel={t(`${T_BASE}.buttons.close`)}
        icon={<img src={thanksIcon} alt="" width="56" height="56"/>}
        iconPlacement="top"
        background={null}
        borderRadius="16px"
        maxWidth="400px"
        title={t(`${T_BASE}.thanks.title`)}
        subtitle={t(`${T_BASE}.thanks.subtitle`)}
      />
    </div>
  )
}

DescriptionForReviewFeedbackForm.propTypes = {
  existingFeedback: PropTypes.shape({
    feedbackData: PropTypes.shape({
      incorrectInformation: PropTypes.bool,
      missingImportantInformation: PropTypes.bool,
      doesNotReflectHotelWell: PropTypes.bool,
      otherComment: PropTypes.string,
    }),
  }),
  onSubmitFeedback: PropTypes.func.isRequired,
  onCheckFactsheet: PropTypes.func,
}

export default DescriptionForReviewFeedbackForm
